import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton, Grid
} from '@mui/material';
import { Home, Print, Assessment, TrendingUp, TrendingDown, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PnLByDimension = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [dims, setDims]       = useState([]);
  const [dimension, setDim]   = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/finance/dimensions`, { headers: hdrs() })
      .then(r => { setDims(r.data || []); if (r.data?.length) setDim(r.data[0].code); })
      .catch(() => {});
  }, []);

  const run = useCallback(async () => {
    if (!dimension) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/pnl-by-dimension`, { headers: hdrs(), params: { dimension, from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to run P&L'); }
    finally { setLoading(false); }
  }, [dimension, from, to]);

  const netColor = (n) => (n >= 0 ? 'success.main' : 'error.main');

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'P&L BY DIMENSION'],
      ['P&L BY DIMENSION'],
      [`Dimension: ${data.dimension}`],
      [`Period: ${from || 'Start'} to ${to || 'End'}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      [`${data.dimension} Value`, 'Name', 'Income', 'Expense', 'Net Profit'],
      ...data.rows.map(r => [r.value, r.name, r.income, r.expense, r.net]),
      [],
      ['Totals', '', data.totals.income, data.totals.expense, data.totals.net],
    ];
    downloadCsv(rows, `pnl_by_${data.dimension}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <Assessment sx={{ mr: 1, color: '#2e7d32' }} />
              <Typography variant="h5" sx={{ color: '#2e7d32' }}>P&amp;L by Dimension</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
                sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
              <IconButton onClick={() => window.print()} title="Print" disabled={!data}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
              <IconButton onClick={() => navigate('/finance')} title="Finance Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Income and expense (income-statement accounts) split by a dimension's values. Postings with no
            value for this dimension fall under "(none)".
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2} className="no-print">
            <TextField select label="Dimension" value={dimension} size="small" sx={{ minWidth: 180 }}
              onChange={e => setDim(e.target.value)}>
              {dims.map(d => <MenuItem key={d.code} value={d.code}>{d.code} — {d.name}</MenuItem>)}
            </TextField>
            <TextField type="date" label="From" value={from} size="small" onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="To" value={to} size="small" onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <IconButton onClick={run} disabled={loading || !dimension} color="primary" sx={{ bgcolor: '#2e7d3215' }}>
              {loading ? <CircularProgress size={20} /> : <Assessment />}
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {data && (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Card elevation={2} sx={{ borderLeft: '4px solid #2e7d32' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box><Typography variant="h6" fontWeight={800} color="#2e7d32" lineHeight={1}>{sym()} {fmt(data.totals.income)}</Typography>
                          <Typography variant="caption" color="text.secondary">Total Income</Typography></Box>
                        <TrendingUp sx={{ color: '#2e7d32' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card elevation={2} sx={{ borderLeft: '4px solid #c62828' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box><Typography variant="h6" fontWeight={800} color="#c62828" lineHeight={1}>{sym()} {fmt(data.totals.expense)}</Typography>
                          <Typography variant="caption" color="text.secondary">Total Expense</Typography></Box>
                        <TrendingDown sx={{ color: '#c62828' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card elevation={2} sx={{ borderLeft: `4px solid ${data.totals.net >= 0 ? '#1565c0' : '#c62828'}` }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="h6" fontWeight={800} color={data.totals.net >= 0 ? '#1565c0' : '#c62828'} lineHeight={1}>
                        {sym()} {fmt(data.totals.net)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Net Profit</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {data.rows.length === 0 ? <Typography color="text.secondary">No income-statement postings in this period.</Typography> : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{data.dimension} Value</strong></TableCell>
                        <TableCell><strong>Name</strong></TableCell>
                        <TableCell align="right"><strong>Income</strong></TableCell>
                        <TableCell align="right"><strong>Expense</strong></TableCell>
                        <TableCell align="right"><strong>Net Profit</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.rows.map(r => (
                        <TableRow key={r.value} hover>
                          <TableCell><strong>{r.value}</strong></TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell align="right">{fmt(r.income)}</TableCell>
                          <TableCell align="right">{fmt(r.expense)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: netColor(r.net) }}>{fmt(r.net)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Totals</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(data.totals.income)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(data.totals.expense)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: netColor(data.totals.net) }}>{sym()} {fmt(data.totals.net)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PnLByDimension;
